import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Checkbox,
  Card,
  message,
  Typography,
  Space,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  BookOutlined,
  WechatOutlined,
} from '@ant-design/icons';
import { login, getCurrentUser } from '../../services/auth';
import { saveAuthData, isAuthenticated, setUserInfo } from '../../utils/auth';
import { weChatLogin } from '../../services/wechat';

const { Title, Text } = Typography;

interface LoginForm {
  username: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated()) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  // 自动检测企业微信环境并处理登录
  React.useEffect(() => {
    const autoWeChatLogin = async () => {
      // 如果已登录，不处理
      if (isAuthenticated()) {
        return;
      }

      const ua = navigator.userAgent.toLowerCase();
      const isInWeChatWork = ua.includes('wxwork');
      
      console.log('=== 登录页面加载 ===');
      console.log('User Agent:', ua);
      console.log('是否在企业微信内:', isInWeChatWork);
      
      if (isInWeChatWork) {
        // 检查 URL 中是否有 code
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        console.log('URL 参数:', window.location.search);
        console.log('Code:', code);
        
        if (code) {
          // 有 code，自动处理登录
          console.log('检测到企业微信 code，自动登录...');
          setWechatLoading(true);
          
          try {
            const { weChatOAuthCallback } = await import('../../services/wechat');
            const response = await weChatOAuthCallback(code);
            
            if (response.success && response.access_token) {
              saveAuthData({
                access_token: response.access_token,
                refresh_token: response.refresh_token || response.access_token,
                token_type: response.token_type || 'bearer',
              });
              
              if (response.user_info) {
                setUserInfo({
                  id: response.user_info.id,
                  username: response.user_info.username,
                  email: response.user_info.email,
                  name: response.user_info.real_name,
                  role: response.user_info.role as any,
                  avatar: response.user_info.wechat_avatar,
                  permissions: [],
                });
              }
              
              message.success('企业微信登录成功！');
              navigate('/');
            } else {
              message.error(response.message || '企业微信登录失败');
              setWechatLoading(false);
            }
          } catch (error: any) {
            console.error('企业微信自动登录失败:', error);
            message.error(error.message || '企业微信登录失败');
            setWechatLoading(false);
          }
        } else {
          // 没有 code，自动跳转获取授权（静默授权）
          console.log('企业微信环境但无 code，自动跳转获取授权...');
          setWechatLoading(true);
          
          try {
            const { getWeChatOAuthUrl } = await import('../../services/wechat');
            const protocol = window.location.protocol;
            const host = window.location.host;
            const currentUrl = `${protocol}//${host}/login`;
            
            const response = await getWeChatOAuthUrl(currentUrl, 'STATE', 'snsapi_base');
            
            if (response.authorize_url) {
              console.log('跳转到授权页面:', response.authorize_url);
              window.location.href = response.authorize_url;
            } else {
              console.error('无法获取授权 URL');
              setWechatLoading(false);
            }
          } catch (error: any) {
            console.error('获取授权 URL 失败:', error);
            setWechatLoading(false);
          }
        }
      }
    };
    
    autoWeChatLogin();
  }, [navigate]);


  const handleSubmit = async (values: LoginForm) => {
    setLoading(true);
    try {
      const response = await login({
        username: values.username,
        password: values.password,
        remember: values.remember,
      });

      // Save authentication data
      saveAuthData(response);

      // Fetch and save user info
      try {
        const userInfo = await getCurrentUser();
        setUserInfo(userInfo);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }

      message.success('登录成功！');

      // Redirect to intended page or role-based default page
      const from = (location.state as any)?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('Login error:', error);
      message.error(error.message || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleWeChatLogin = async () => {
    setWechatLoading(true);
    try {
      await weChatLogin();
    } catch (error: any) {
      console.error('WeChat login error:', error);
      message.error(error.message || '企业微信登录失败');
      setWechatLoading(false);
    }
    // Note: wechatLoading will be reset when page redirects or user returns
  };

  return (
    <div className="login-container">
      <Card className="login-box">
        <div className="login-logo">
          <Space direction="vertical" align="center" size="small">
            <BookOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
              法律事务所CRM
            </Title>
            <Text type="secondary">专业的法律案件管理系统</Text>
          </Space>
        </div>

        <Form
          form={form}
          name="login"
          className="login-form"
          onFinish={handleSubmit}
          autoComplete="off"
          size="large"
          initialValues={{
            remember: true,
            username: '',
            password: '',
          }}
        >
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                message: '请输入用户名！',
              },
              {
                min: 3,
                message: '用户名至少3个字符！',
              },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              {
                required: true,
                message: '请输入密码！',
              },
              {
                min: 6,
                message: '密码至少6个字符！',
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox>记住我</Checkbox>
              </Form.Item>
              <Button
                type="link"
                onClick={() => message.info('忘记密码功能开发中...')}
                style={{ padding: 0 }}
              >
                忘记密码？
              </Button>
            </div>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="login-button"
              loading={loading}
              block
            >
              {loading ? '登录中...' : '登录'}
            </Button>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="default"
              icon={<WechatOutlined />}
              onClick={handleWeChatLogin}
              loading={wechatLoading}
              block
              style={{
                backgroundColor: '#07c160',
                borderColor: '#07c160',
                color: 'white',
              }}
            >
              {wechatLoading ? '跳转中...' : '企业微信登录'}
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            还没有账号？
            <Button
              type="link"
              onClick={() => message.info('注册功能开发中...')}
              style={{ padding: '0 4px', fontSize: 12 }}
            >
              立即注册
            </Button>
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Login;